// Colorway management, controlled by App (single source of truth).
// Previously this component kept its own fake list in local state; now every
// add / delete / apply / rename goes through App so it can be kept in sync
// with the worker (RECOLOR) and with undo/redo.
//
// Signature rule: switching colorways NEVER touches the alpha maps — only the
// palette colors change. The hint at the bottom states this explicitly.

import React, { useState } from 'react';
import type { PaletteColor } from '@imagelayerdecomposer/core';
import { labToHex } from '@imagelayerdecomposer/core';

export interface ColorwayEntry {
  id: string;
  name: string;
  colors: PaletteColor[];
}

interface ColorwaysPanelProps {
  colorways: ColorwayEntry[];
  activeId: string;
  onApply: (id: string) => void;
  onDuplicate: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
}

function swatchHex(color: PaletteColor): string {
  try {
    return labToHex(color.lab);
  } catch {
    return '#888888';
  }
}

export default function ColorwaysPanel({
  colorways,
  activeId,
  onApply,
  onDuplicate,
  onNew,
  onDelete,
  onRename,
}: ColorwaysPanelProps) {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  const startRename = (cw: ColorwayEntry) => {
    setRenamingId(cw.id);
    setDraftName(cw.name);
  };

  const commitRename = () => {
    if (!renamingId) return;
    const trimmed = draftName.trim();
    if (trimmed) onRename(renamingId, trimmed);
    setRenamingId(null);
  };

  return (
    <div className="panel">
      <div className="panel-header">
        <span>Colorways</span>
        <span className="count-badge">{colorways.length}</span>
      </div>

      <div className="colorway-list">
        {colorways.map((cw) => {
          const isActive = cw.id === activeId;
          return (
            <div
              key={cw.id}
              className={`colorway-card ${isActive ? 'active' : ''}`}
              onClick={() => onApply(cw.id)}
            >
              <div className="colorway-header">
                {renamingId === cw.id ? (
                  <input
                    type="text"
                    value={draftName}
                    autoFocus
                    className="colorway-name-input"
                    onChange={(e) => setDraftName(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename();
                      if (e.key === 'Escape') setRenamingId(null);
                      // Do not let the card's onClick/apply run while typing.
                      e.stopPropagation();
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span
                    className="colorway-name"
                    title="Double-click to rename"
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      startRename(cw);
                    }}
                  >
                    {cw.name}
                  </span>
                )}
                {isActive && <span className="colorway-active-tag">active</span>}
              </div>

              <div className="swatch-strip">
                {cw.colors.map((color) => (
                  <div
                    key={color.id}
                    className="swatch"
                    style={{ backgroundColor: swatchHex(color) }}
                    title={`${color.isPaper ? 'Paper' : 'Layer'} ${swatchHex(color)}`}
                  />
                ))}
              </div>

              <div className="colorway-actions">
                <button
                  className="btn small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onApply(cw.id);
                  }}
                  disabled={isActive}
                >
                  Apply
                </button>
                <button
                  className="btn small secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate(cw.id);
                  }}
                  title="Duplicate this colorway"
                >
                  Dup
                </button>
                <button
                  className="btn small secondary danger-text"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(cw.id);
                  }}
                  disabled={colorways.length <= 1}
                  title={
                    colorways.length <= 1
                      ? 'At least one colorway must remain'
                      : 'Delete this colorway'
                  }
                >
                  Del
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <button className="btn new-colorway" onClick={onNew}>
        + New Colorway
      </button>

      <p className="hint">Alpha maps never change when switching colorways.</p>
    </div>
  );
}
